function toWorkingLines(coverage) {
    return coverage.map((line) => ({
        position: line.position,
        near: new Set(line.near),
        blocked: line.blocked,
        far: new Set(line.far),
    }));
}
/**
 * Splits a list of candidate indices (ascending) into runs of consecutive
 * line indices — candidates tied on score can come from unrelated clusters
 * of foundations elsewhere in the building, so only indices adjacent in the
 * line sequence represent a genuine "neighboring lines" tie.
 */
function groupIntoConsecutiveRuns(indices) {
    const runs = [];
    for (const idx of indices) {
        const lastRun = runs[runs.length - 1];
        if (lastRun !== undefined && idx === lastRun[lastRun.length - 1] + 1) {
            lastRun.push(idx);
        }
        else {
            runs.push([idx]);
        }
    }
    return runs;
}
/**
 * Picks the winning line for the current round:
 *  1. Highest near.size + far.size.
 *  2. Ties broken by fewest blocked.
 *  3. Remaining ties grouped into runs of neighboring lines (since unrelated
 *     ties elsewhere in the building carry no positional relationship); the
 *     first such run is taken, and its middle line is the winner.
 */
function pickWinner(lines) {
    let maxScore = 0;
    for (const line of lines)
        maxScore = Math.max(maxScore, line.near.size + line.far.size);
    if (maxScore === 0)
        return null;
    const topScored = lines
        .map((line, index) => ({ line, index }))
        .filter(({ line }) => line.near.size + line.far.size === maxScore);
    let minBlocked = Infinity;
    for (const { line } of topScored)
        minBlocked = Math.min(minBlocked, line.blocked.length);
    const candidateIndices = topScored
        .filter(({ line }) => line.blocked.length === minBlocked)
        .map(({ index }) => index);
    const [firstRun] = groupIntoConsecutiveRuns(candidateIndices);
    const run = firstRun;
    const winnerIndex = run[Math.floor(run.length / 2)];
    return { winner: lines[winnerIndex], index: winnerIndex };
}
/** Removes `ids` from every line's `near`/`far` set — called once a foundation's coverage is claimed. */
function removeFromAllLines(lines, key, ids) {
    for (const id of ids) {
        for (const line of lines)
            line[key].delete(id);
    }
}
/**
 * Greedily selects grid lines along one axis until every line's near and far
 * sets are empty. Each round: pick the current best line (see pickWinner),
 * snapshot its near/blocked/far state, then strip the ids it claimed from
 * every other line's near/far sets so they aren't claimed again.
 */
export function selectGridLinesForAxis(coverage) {
    const lines = toWorkingLines(coverage);
    const selected = [];
    while (true) {
        const picked = pickWinner(lines);
        if (picked === null)
            break;
        const { winner } = picked;
        const near = [...winner.near];
        const blocked = [...winner.blocked];
        const far = [...winner.far];
        selected.push({ position: winner.position, near, blocked, far });
        removeFromAllLines(lines, "near", near);
        removeFromAllLines(lines, "far", far);
    }
    return selected;
}
/** Runs selectGridLinesForAxis independently for the x and y candidate lines. */
export function selectGridLines(coverage) {
    return {
        x: selectGridLinesForAxis(coverage.x),
        y: selectGridLinesForAxis(coverage.y),
    };
}
